import * as Tooltip from "@radix-ui/react-tooltip";
import type { ReactNode } from "react";

import styles from "./TooltipV2.module.css";

export type TooltipV2Side = "top" | "right" | "bottom" | "left";

/**
 * Provider único que debe envolver a los TooltipV2 desde un nivel superior.
 * Es quien coordina los delays entre tooltips hermanos (`skipDelayDuration`):
 * montar uno por tooltip rompe esa coordinación y multiplica los portales.
 */
export const TooltipV2Provider = ({ children }: { children: ReactNode }) => (
  <Tooltip.Provider delayDuration={200} skipDelayDuration={300}>
    {children}
  </Tooltip.Provider>
);

interface Props {
  title: string;
  subtitle?: string;
  /**
   * Lista de líneas a mostrar en el cuerpo (p.ej. varias alarmas). Cuando se
   * pasa, sustituye a `subtitle` en el render; `title` sigue siendo el aria-label.
   */
  lines?: string[];
  side?: TooltipV2Side;
  defaultOpen?: boolean;
  /**
   * Usa el hijo como trigger (Radix `asChild`) en vez del `<button>` propio del
   * tooltip. Necesario cuando el hijo ya es interactivo (p. ej. `IconButtonV2`)
   * para no anidar dos `<button>`. El hijo aporta su propio `aria-label`.
   */
  asChild?: boolean;
  /**
   * Renderiza el trigger como `<span>` en vez del `<button>` por defecto. Necesario
   * cuando el tooltip vive dentro de un ancestro interactivo (p.ej. una fila `<a>`),
   * donde un `<button>` sería HTML inválido y robaría el click de navegación.
   */
  triggerInline?: boolean;
  children: ReactNode;
}

export const TooltipV2 = ({
  title,
  subtitle,
  lines,
  side = "right",
  defaultOpen,
  asChild,
  triggerInline = false,
  children,
}: Props) => {
  let trigger;
  if (asChild) {
    trigger = <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>;
  } else if (triggerInline) {
    trigger = (
      <Tooltip.Trigger asChild>
        <span className={styles.trigger} aria-label={title}>
          {children}
        </span>
      </Tooltip.Trigger>
    );
  } else {
    trigger = (
      <Tooltip.Trigger
        type="button"
        className={styles.trigger}
        aria-label={title}
      >
        {children}
      </Tooltip.Trigger>
    );
  }

  return (
    <Tooltip.Root defaultOpen={defaultOpen}>
      {trigger}
      <Tooltip.Portal>
        <Tooltip.Content className={styles.content} side={side} sideOffset={6}>
          {lines != null && lines.length > 0 ? (
            lines.map((line) => (
              <span key={line} className={styles.title}>
                {line}
              </span>
            ))
          ) : (
            <>
              <span className={styles.title}>{title}</span>
              {subtitle != null && subtitle !== "" ? (
                <span className={styles.subtitle}>{subtitle}</span>
              ) : null}
            </>
          )}
          <Tooltip.Arrow className={styles.arrow} width={8} height={6} />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
};
